import { useEffect, useMemo, useState } from "react";
import { useTimelineStore } from "../../state";
import { List, TimelineListWrapper } from "./elements";
import { generateTimelineItems } from "./generateTimelineItems";
import { TimelineActionGroup } from "./TimelineActionGroup";
import type { TimelineListProps } from "./types";
import { useScrollHandler } from "./useScrollHandler";

export function TimelineList({
    gaiaPayloadProps,
    isExternal = false,
    logger,
    scrollRef,
    IssueSummaryComponent,
}: TimelineListProps) {
    const { markAllMessagesAsRead, getTimelineState, currentSessionId } = useTimelineStore();
    const { messages, autoCompleteSuggestions } = getTimelineState(currentSessionId);
    const { mergedRef, isScrollAtBottom } = useScrollHandler({ scrollRef, messages: messages.length });
    const [shouldShowNewMessageButton, setShouldShowNewMessageButton] = useState(false);

    const timelineItems = useMemo(
        () =>
            generateTimelineItems({
                isExternal,
                logger,
                messages,
                scrollRef,
                gaiaPayloadProps,
            }),
        [messages, scrollRef, logger, isExternal, gaiaPayloadProps],
    );

    const unreadMessages = useMemo(() => messages.filter((message) => message.isUnread).length, [messages]);

    useEffect(() => {
        setShouldShowNewMessageButton(unreadMessages > 0 && !isScrollAtBottom);
        if (isScrollAtBottom && unreadMessages > 0) {
            markAllMessagesAsRead(currentSessionId);
        }
    }, [unreadMessages, isScrollAtBottom, markAllMessagesAsRead, currentSessionId]);

    return (
        <>
            <TimelineListWrapper mergedRef={mergedRef}>
                <List data-testid="timeline-list" IssueSummaryComponent={IssueSummaryComponent}>
                    {timelineItems}
                </List>
            </TimelineListWrapper>
            <TimelineActionGroup
                gaiaPayloadProps={gaiaPayloadProps}
                isExternal={isExternal}
                logger={logger}
                scrollRef={scrollRef}
                shouldShowNewMessageButton={shouldShowNewMessageButton}
                unreadMessages={unreadMessages}
                proactiveSuggestions={autoCompleteSuggestions}
            />
        </>
    );
}
